import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner,
} from '@monksflow/monks-ui';
import { FileTextIcon } from 'lucide-react';
import 'pdfjs-dist/web/pdf_viewer.css';
import { useAnnotationFullscreen, useAnnotationSolo } from '@/features/comment-on-file';
import type { AssetFile } from '../../assetDetail.types';
import { FullscreenToggle } from '../Fullscreen';
import { ControlGroup, ControlGroupSeparator, HoverControlsPill } from '../HoverControlsPill';
import { ZoomControls } from '../ZoomControls';
import { usePdfDocument } from './hooks/usePdfDocument';
import { PdfPage } from './PdfPage';
import { PdfPageNavigator } from './PdfPageNavigator';
import { PdfPageOverlay } from './PdfPageOverlay';
import { PDF_MAX_ZOOM, PDF_MIN_ZOOM, usePdfViewerStore } from './pdfViewer.store';
import { useTranslation } from 'react-i18next';

interface Props {
  file: AssetFile;
  /** When false, suppress per-page PdfPageOverlay (no draft drawing, no
   * persisted shapes). Zoom + scroll-to-page still work. */
  annotationsEnabled?: boolean;
  /** When false, hide the Expand/fullscreen toggle. Fullscreen relies on an
   * `AssetDetailFullScreen` host that only the AssetDetail page mounts; in
   * embedded previews (e.g. the Library modal) the toggle would just flip the
   * global flag and hide the controls without opening anything. */
  showFullscreen?: boolean;
}

const PAGE_GAP_PX = 12;
const HORIZONTAL_PADDING_PX = 16;
/** Pixels from the scroll container's top below which a page counts as "current". */
const CURRENT_PAGE_TOP_TOLERANCE_PX = 80;
/** Quiet period a width change must survive before the pages re-raster at it. */
const RESIZE_SETTLE_MS = 120;

/**
 * Renders a PDF in the AssetDetail preview area. Pages stack vertically and
 * fit to the container's width; canvas + text-layer rendering is lazy via
 * IntersectionObserver in PdfPage. Annotation overlays are layered in by a
 * sibling subtask (Stage 3).
 */
export function PdfViewer({ file, annotationsEnabled = true, showFullscreen = true }: Props) {
  const { t } = useTranslation('projects');

  const { pdf, pages, loading, error } = usePdfDocument(file.downloadUrl);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  const [containerWidth, setContainerWidth] = useState(0);
  const setCurrentPage = usePdfViewerStore((s) => s.setCurrentPage);
  const setTotalPages = usePdfViewerStore((s) => s.setTotalPages);
  const setGoToPage = usePdfViewerStore((s) => s.setGoToPage);
  const resetPdfStore = usePdfViewerStore((s) => s.reset);
  const zoomLevel = usePdfViewerStore((s) => s.zoomLevel);
  const zoomIn = usePdfViewerStore((s) => s.zoomIn);
  const zoomOut = usePdfViewerStore((s) => s.zoomOut);
  const resetZoom = usePdfViewerStore((s) => s.resetZoom);
  const { soloCommentId, soloAnnotation } = useAnnotationSolo();
  const { isFullscreen } = useAnnotationFullscreen();
  // Multi-page annotations: focus jumps to the topmost (lowest-numbered) page.
  const soloPdfPage = useMemo(() => {
    if (!soloAnnotation || soloAnnotation.kind !== 'pdf' || soloAnnotation.shapes.length === 0) {
      return null;
    }
    return Math.min(...soloAnnotation.shapes.map((s) => s.page));
  }, [soloAnnotation]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => {
      const inner = el.clientWidth - HORIZONTAL_PADDING_PX * 2;
      setContainerWidth(inner > 0 ? inner : 0);
    };
    // First paint measures synchronously; only observer-driven updates settle.
    // A new width re-rasters every intersecting page, so tracking an animating
    // sidebar or a resize drag live would blank the preview for its duration.
    measure();
    let settle = 0;
    const ro = new ResizeObserver(() => {
      clearTimeout(settle);
      settle = window.setTimeout(measure, RESIZE_SETTLE_MS);
    });
    ro.observe(el);
    return () => {
      clearTimeout(settle);
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    setTotalPages(pages.length);
  }, [pages, setTotalPages]);

  // Track the current page based on scroll: the topmost page whose top edge
  // is at or above the container's top (with a small tolerance).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || pages.length === 0) return;

    let frame = 0;
    const compute = () => {
      const containerTop = el.getBoundingClientRect().top + CURRENT_PAGE_TOP_TOLERANCE_PX;
      let next = 1;
      for (const meta of pages) {
        const pageEl = pageRefs.current.get(meta.pageNumber);
        if (!pageEl) continue;
        if (pageEl.getBoundingClientRect().top > containerTop) break;
        next = meta.pageNumber;
      }
      setCurrentPage(next);
    };

    const onScroll = () => {
      if (frame !== 0) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        compute();
      });
    };

    compute();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, [pages, setCurrentPage]);

  // Stable goToPage handle, registered with the store so the toolbar's
  // PdfPageNavigator can drive the viewer.
  const goToPage = useCallback((pageNumber: number) => {
    const total = pagesRef.current.length;
    if (total === 0) return;
    const clamped = Math.max(1, Math.min(total, pageNumber));
    const el = pageRefs.current.get(clamped);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    setGoToPage(goToPage);
    return () => resetPdfStore();
  }, [goToPage, setGoToPage, resetPdfStore]);

  // Scroll the soloed PDF annotation's page into view whenever the soloed
  // comment changes. `pages.length` is in deps so deep-linked / pre-mount
  // solo state retries the scroll once pages register in pageRefs.
  useEffect(() => {
    if (!soloCommentId || soloPdfPage === null) return;
    const el = pageRefs.current.get(soloPdfPage);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [soloCommentId, soloPdfPage, pages.length]);

  const pageScales = useMemo(
    () =>
      pages.map((p) =>
        containerWidth > 0 && p.width > 0 ? (containerWidth / p.width) * zoomLevel : 1,
      ),
    [pages, containerWidth, zoomLevel],
  );

  const canZoomIn = zoomLevel < PDF_MAX_ZOOM - 1e-6;
  const canZoomOut = zoomLevel > PDF_MIN_ZOOM + 1e-6;
  const canResetZoom = zoomLevel !== 1;

  if (error === 'password') {
    return <PdfErrorState message={t('projectDetail.pdfViewer.passwordProtectedPdfsArenT')} />;
  }
  if (error === 'load') {
    return <PdfErrorState message={t('projectDetail.pdfViewer.thisPdfCouldnTBe')} />;
  }

  return (
    <div className="group relative flex min-h-0 min-w-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        className="min-h-0 min-w-0 flex-1 overflow-auto rounded-tl-lg rounded-tr-lg bg-muted dark:bg-background"
      >
        {loading && (
          <div
            aria-busy="true"
            aria-live="polite"
            className="flex h-full items-center justify-center p-8"
          >
            <Spinner />
          </div>
        )}

        {!loading && pdf && containerWidth > 0 && (
          // `w-max min-w-full` lets the wrapper grow past the viewport when
          // zoomed in (so horizontal scroll works) while still spanning the
          // full width at zoom <= 1 so `items-center` can center the pages.
          <div
            className="mx-auto flex w-max min-w-full flex-col items-center py-4"
            style={{
              gap: PAGE_GAP_PX,
              paddingLeft: HORIZONTAL_PADDING_PX,
              paddingRight: HORIZONTAL_PADDING_PX,
            }}
          >
            {pages.map((meta, i) => {
              const scale = pageScales[i];
              return (
                <PdfPage
                  key={meta.pageNumber}
                  ref={(el) => {
                    if (el) {
                      pageRefs.current.set(meta.pageNumber, el);
                    } else {
                      pageRefs.current.delete(meta.pageNumber);
                    }
                  }}
                  pdf={pdf}
                  pageNumber={meta.pageNumber}
                  cssWidth={meta.width * scale}
                  cssHeight={meta.height * scale}
                  cssScale={scale}
                  scrollRoot={scrollRef.current}
                  forceRender={soloPdfPage === meta.pageNumber}
                >
                  {annotationsEnabled && <PdfPageOverlay page={meta.pageNumber} />}
                </PdfPage>
              );
            })}
          </div>
        )}
      </div>
      {pdf && !isFullscreen && (
        <HoverControlsPill className="absolute bottom-4 left-4">
          <ControlGroup>
            <PdfPageNavigator />
            <ControlGroupSeparator />
            <ZoomControls
              zoom={zoomLevel}
              canZoomIn={canZoomIn}
              canZoomOut={canZoomOut}
              canReset={canResetZoom}
              onZoomIn={zoomIn}
              onZoomOut={zoomOut}
              onReset={resetZoom}
            />
            {showFullscreen && (
              <>
                <ControlGroupSeparator />
                <FullscreenToggle />
              </>
            )}
          </ControlGroup>
        </HoverControlsPill>
      )}
    </div>
  );
}

function PdfErrorState({ message }: { message: string }) {
  const { t } = useTranslation('projects');

  return (
    <Empty role="status" className="min-h-0 min-w-0 flex-1">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <FileTextIcon className="size-6 text-muted-foreground" />
        </EmptyMedia>
        <EmptyTitle>{t('projectDetail.pdfViewer.pdfUnavailable')}</EmptyTitle>
        <EmptyDescription>{message}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}
